import React, { useEffect, useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { CheckCircle2, ShieldAlert, Send } from "lucide-react";
import { useAuth } from "../context/AuthContext";

export default function VerifyEmail() {
  const [searchParams] = useSearchParams();
  const { user, refresh } = useAuth();
  const [state, setState] = useState({ status: "working", message: "" });
  const [resending, setResending] = useState(false);
  const [resent, setResent] = useState(false);

  useEffect(() => {
    const t = searchParams.get("token");
    if (!t) { setState({ status: "no_token", message: "" }); return; }
    (async () => {
      try {
        const { data } = await api.post("/auth/verify-email", { token: t });
        setState({ status: "done", message: data.email });
        if (user) await refresh();
      } catch (e) {
        setState({ status: "failed", message: formatApiError(e.response?.data?.detail) || e.message });
      }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams]);

  const resend = async () => {
    setResending(true);
    try { await api.post("/auth/resend-verification"); setResent(true); }
    catch (e) { setState({ status: "failed", message: formatApiError(e.response?.data?.detail) || e.message }); }
    finally { setResending(false); }
  };

  return (
    <div className="max-w-xl mx-auto px-6 py-20 text-center">
      {state.status === "working" && (
        <>
          <ShieldAlert className="w-10 h-10 mx-auto text-[#5C5C5C]" />
          <h1 className="font-display font-black text-3xl mt-5">Verifying your email…</h1>
        </>
      )}
      {state.status === "done" && (
        <>
          <CheckCircle2 className="w-14 h-14 mx-auto text-[var(--brand-primary)]" />
          <h1 className="font-display font-black text-4xl mt-5 tracking-tight">Email verified.</h1>
          <p className="text-[#5C5C5C] mt-3">{state.message} is now confirmed. You're all set.</p>
          <Link to="/" data-testid="verify-home" className="btn-terra mt-8 inline-flex">Continue</Link>
        </>
      )}
      {state.status === "failed" && (
        <>
          <ShieldAlert className="w-10 h-10 mx-auto text-[var(--brand-primary)]" />
          <h1 className="font-display font-black text-3xl mt-5">Verification failed.</h1>
          <p className="text-[var(--brand-primary)] mt-3" data-testid="verify-error">{state.message}</p>
          {user && (
            <button onClick={resend} disabled={resending || resent} data-testid="resend-verify" className="btn-ink mt-6 inline-flex">
              <Send className="w-4 h-4" /> {resent ? "New email sent — check your inbox" : (resending ? "Sending…" : "Send me a new link")}
            </button>
          )}
        </>
      )}
      {state.status === "no_token" && (
        <>
          <ShieldAlert className="w-10 h-10 mx-auto text-[#5C5C5C]" />
          <h1 className="font-display font-black text-3xl mt-5">Missing token.</h1>
          <p className="text-[#5C5C5C] mt-3">Click the verification link in your inbox. If you can't find it:</p>
          {user && (
            <button onClick={resend} disabled={resending || resent} data-testid="resend-verify" className="btn-ink mt-6 inline-flex">
              <Send className="w-4 h-4" /> {resent ? "New email sent — check your inbox" : (resending ? "Sending…" : "Send me a new link")}
            </button>
          )}
        </>
      )}
    </div>
  );
}
